/**
 * Anchored paths (D140, extracted for D175): a document path with its
 * top-level index replaced by the entity's own id.
 *
 * @remarks
 * `rooms[2].description.variants[0]` names a place in the document that
 * moves the day a room is inserted above it; `rooms[room_checkpoint]…`
 * names the same place by the thing it is about, which is the identity a
 * warning baseline (D141) can key on. Two warnings anchor their paths this
 * way now, so the reading lives once.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

/**
 * Replace a path's leading `collection[index]` with `collection[<id>]`,
 * reading the id off the element itself — its one `…Id` key whose value is a
 * string. A path that does not start that way is returned unchanged.
 *
 * @param contentPack - The parsed pack
 * @param path - An index-addressed path
 * @returns The same path, anchored on the entity's id where it can be
 */
export const anchorPath = (contentPack: ContentPackDefinition, path: string): string => {
	// Named groups would read better, and they type as `string | undefined`
	// even when the pattern guarantees them — so every use needs a `?? ''` the
	// regex has already made unreachable, and three dead branches is a worse
	// price than one slice. `head[0]` is declared `string` on `RegExpExecArray`,
	// so the whole matched head comes back without an optionality to launder.
	const head = /^[a-zA-Z]+\[\d+\]/u.exec(path);

	if (head === null) {
		return path;
	}

	const matched = head[0];
	const openBracket = matched.indexOf('[');
	const collection = matched.slice(0, openBracket);
	const index = matched.slice(openBracket + 1, matched.length - 1);
	const rest = path.slice(matched.length);
	const elements = (contentPack as unknown as Record<string, unknown>)[collection];
	const element = Array.isArray(elements) ? (elements[Number(index)] as unknown) : undefined;
	const record = typeof element === 'object' && element !== null ? (element as Record<string, unknown>) : {};
	const idEntry = Object.entries(record).find(([key, value]) => /^[a-z]+Id$/u.test(key) && typeof value === 'string');

	return idEntry === undefined ? path : `${collection}[${String(idEntry[1])}]${rest}`;
};

